import { useEffect, useState, useCallback } from 'react';
import { Cookie, X, Shield, Gear, ChartBar, Megaphone } from '@phosphor-icons/react';
import api from '@/lib/api';

const POLICY_VERSION = '2026-06-01';
const STORAGE_KEY = 'ihtp_cookie_consent';
const POLICY_URL = 'https://ihavethepower.net/politica-de-cookies/';

const T = {
  pt: {
    title: 'Respeitamos a sua privacidade',
    body: 'Utilizamos cookies para garantir o funcionamento do site, melhorar a sua experiência, analisar o tráfego e personalizar conteúdos. Pode aceitar todos, rejeitar os não essenciais ou escolher as suas preferências.',
    acceptAll: 'Aceitar tudo',
    reject: 'Rejeitar não essenciais',
    customize: 'Personalizar',
    save: 'Guardar preferências',
    policy: 'Política de cookies',
    prefsTitle: 'Preferências de cookies',
    always: 'Sempre ativo',
    cats: {
      necessary: ['Necessárias', 'Essenciais para o funcionamento do site, sessão e segurança. Não podem ser desativadas.'],
      functional: ['Funcionais', 'Permitem recordar as suas escolhas (idioma, preferências) para uma melhor experiência.'],
      statistics: ['Estatística', 'Ajudam-nos a perceber como os visitantes utilizam o site, de forma anónima.'],
      marketing: ['Marketing', 'Usadas para apresentar conteúdos e campanhas relevantes para si.'],
    },
    manage: 'Gerir cookies',
  },
  en: {
    title: 'We respect your privacy',
    body: 'We use cookies to keep the site working, improve your experience, analyse traffic and personalise content. You can accept all, reject non-essential ones, or choose your preferences.',
    acceptAll: 'Accept all',
    reject: 'Reject non-essential',
    customize: 'Customize',
    save: 'Save preferences',
    policy: 'Cookie policy',
    prefsTitle: 'Cookie preferences',
    always: 'Always on',
    cats: {
      necessary: ['Necessary', 'Essential for the website to function, sessions and security. These cannot be disabled.'],
      functional: ['Functional', 'Let us remember your choices (language, preferences) for a better experience.'],
      statistics: ['Statistics', 'Help us understand how visitors use the site, anonymously.'],
      marketing: ['Marketing', 'Used to show content and campaigns relevant to you.'],
    },
    manage: 'Manage cookies',
  },
  es: {
    title: 'Respetamos su privacidad',
    body: 'Utilizamos cookies para el funcionamiento del sitio, mejorar su experiencia, analizar el tráfico y personalizar contenidos. Puede aceptar todas, rechazar las no esenciales o elegir sus preferencias.',
    acceptAll: 'Aceptar todo',
    reject: 'Rechazar no esenciales',
    customize: 'Personalizar',
    save: 'Guardar preferencias',
    policy: 'Política de cookies',
    prefsTitle: 'Preferencias de cookies',
    always: 'Siempre activo',
    cats: {
      necessary: ['Necesarias', 'Esenciales para el funcionamiento del sitio, la sesión y la seguridad. No se pueden desactivar.'],
      functional: ['Funcionales', 'Permiten recordar sus elecciones (idioma, preferencias) para una mejor experiencia.'],
      statistics: ['Estadística', 'Nos ayudan a entender cómo usan el sitio los visitantes, de forma anónima.'],
      marketing: ['Marketing', 'Se usan para mostrar contenidos y campañas relevantes para usted.'],
    },
    manage: 'Gestionar cookies',
  },
};

const CAT_ICONS = { necessary: Shield, functional: Gear, statistics: ChartBar, marketing: Megaphone };

function detectLocale() {
  const l = (navigator.language || 'pt').slice(0, 2).toLowerCase();
  return ['pt', 'en', 'es'].includes(l) ? l : 'pt';
}

function getStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.policy_version === POLICY_VERSION ? p : null;
  } catch { return null; }
}

function getConsentId() {
  let id = localStorage.getItem('ihtp_consent_id');
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('ihtp_consent_id', id);
  }
  return id;
}

export default function CookieConsent() {
  const [locale] = useState(detectLocale);
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, functional: false, statistics: false, marketing: false });
  const t = T[locale];

  useEffect(() => {
    const stored = getStored();
    if (stored) setPrefs(stored.preferences);
    else setVisible(true);
  }, []);

  const persist = useCallback(async (preferences, action) => {
    const record = { preferences, policy_version: POLICY_VERSION, locale, saved_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setPrefs(preferences);
    setVisible(false);
    setShowPrefs(false);
    try {
      await api.post('/consent', { consent_id: getConsentId(), preferences, policy_version: POLICY_VERSION, locale, action });
    } catch { /* o registo falhar não deve bloquear a navegação */ }
  }, [locale]);

  const acceptAll = () => persist({ necessary: true, functional: true, statistics: true, marketing: true }, 'accept_all');
  const rejectAll = () => persist({ necessary: true, functional: false, statistics: false, marketing: false }, 'reject');
  const saveCustom = () => persist({ ...prefs, necessary: true }, 'save');

  // Depois de decidido, não mostramos botão flutuante (ocupava a navegação em
  // mobile). O banner volta a aparecer se a versão da política mudar.
  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4" data-testid="cookie-consent-banner">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#E7E2D6] overflow-hidden">
        {!showPrefs ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0F1829] text-white grid place-items-center flex-shrink-0"><Cookie size={22} /></div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>{t.title}</h2>
                <p className="text-sm text-[#4B5563] mt-1 leading-relaxed">
                  {t.body}{' '}
                  <a href={POLICY_URL} target="_blank" rel="noopener noreferrer" data-testid="cookie-policy-link"
                    className="text-[#0F1829] font-medium underline hover:no-underline">{t.policy}</a>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mt-4">
              <button onClick={() => setShowPrefs(true)} data-testid="cookie-customize-btn"
                className="px-4 py-2.5 text-sm font-medium text-[#0F1829] border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] sm:mr-auto">{t.customize}</button>
              <button onClick={rejectAll} data-testid="cookie-reject-btn"
                className="px-4 py-2.5 text-sm font-medium text-[#0F1829] border border-[#0F1829] rounded-lg hover:bg-[#F3F4F6]">{t.reject}</button>
              <button onClick={acceptAll} data-testid="cookie-accept-all-btn"
                className="px-5 py-2.5 text-sm font-semibold bg-[#0F1829] text-white rounded-lg hover:bg-[#002480]">{t.acceptAll}</button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>{t.prefsTitle}</h2>
              <button onClick={() => setShowPrefs(false)} data-testid="cookie-prefs-close" className="w-8 h-8 rounded-full hover:bg-[#F3F4F6] grid place-items-center"><X size={18} /></button>
            </div>
            <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
              {['necessary', 'functional', 'statistics', 'marketing'].map((key) => {
                const Icon = CAT_ICONS[key];
                const [label, desc] = t.cats[key];
                const locked = key === 'necessary';
                return (
                  <div key={key} className="flex items-start gap-3 border border-[#F1EEE6] rounded-xl p-3" data-testid={`cookie-cat-${key}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#FAF8F2] text-[#0F1829] grid place-items-center flex-shrink-0"><Icon size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F1829]">{label}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                    {locked ? (
                      <span className="text-[11px] font-medium text-emerald-600 whitespace-nowrap mt-1">{t.always}</span>
                    ) : (
                      <button role="switch" aria-checked={prefs[key]} data-testid={`cookie-toggle-${key}`}
                        onClick={() => setPrefs((s) => ({ ...s, [key]: !s[key] }))}
                        className={`relative w-11 h-6 rounded-full flex-shrink-0 mt-0.5 transition-colors ${prefs[key] ? 'bg-[#0F1829]' : 'bg-[#D1D5DB]'}`}>
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${prefs[key] ? 'left-[22px]' : 'left-0.5'}`} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 mt-4">
              <a href={POLICY_URL} target="_blank" rel="noopener noreferrer"
                className="text-sm text-[#6B7280] underline hover:text-[#0F1829] sm:mr-auto self-center">{t.policy}</a>
              <button onClick={rejectAll} data-testid="cookie-prefs-reject-btn"
                className="px-4 py-2.5 text-sm font-medium text-[#0F1829] border border-[#0F1829] rounded-lg hover:bg-[#F3F4F6]">{t.reject}</button>
              <button onClick={saveCustom} data-testid="cookie-save-btn"
                className="px-5 py-2.5 text-sm font-semibold bg-[#0F1829] text-white rounded-lg hover:bg-[#002480]">{t.save}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
